import { useQuery, useQueryClient } from "@tanstack/react-query"

import type { HostedAgentSession, HostedAgentSummary } from "@/api/client/hosted-agents"

import { listHostedAgentsApi, listHostedAgentSessionsApi } from "@/api/client/hosted-agents"
import { queryKeys } from "@/lib/query-keys"

import { isSessionRunActive } from "../utils/agents-utils"

const HOSTED_AGENTS_STALE_TIME = 1000 * 60 * 2

/** Roster query: the hosted agents the current user may see (AccessPolicy-filtered server-side). */
export function useHostedAgents() {
  return useQuery<HostedAgentSummary[]>({
    queryKey: queryKeys.hostedAgents,
    queryFn: () => listHostedAgentsApi(),
    staleTime: HOSTED_AGENTS_STALE_TIME,
  })
}

/**
 * One agent by slug, derived from the roster query (there is no end-user
 * single-agent endpoint — the roster is small and already access-filtered).
 * `data` is `undefined` both while loading and when the slug is unknown;
 * use `isSuccess` to tell "not found" apart from "still loading".
 */
export function useHostedAgent(slug: string | undefined) {
  return useQuery<HostedAgentSummary[], Error, HostedAgentSummary | undefined>({
    queryKey: queryKeys.hostedAgents,
    queryFn: () => listHostedAgentsApi(),
    staleTime: HOSTED_AGENTS_STALE_TIME,
    enabled: !!slug,
    select: (agents) => agents.find((agent) => agent.slug === slug || agent.id === slug),
  })
}

/** Thread list for one agent, newest first. Grouped on stamped `visibility` in the UI (B6). */
/** While any own thread has a run in flight the list polls, so a thread the
 * user is NOT looking at still flips from working to waiting-for-you on its
 * own; an idle list costs nothing. */
export const THREAD_LIST_ACTIVE_POLL_MS = 5000

export function useHostedAgentSessions(slug: string | undefined) {
  return useQuery<HostedAgentSession[]>({
    queryKey: queryKeys.hostedAgentSessions(slug ?? ""),
    queryFn: () => listHostedAgentSessionsApi(slug ?? ""),
    enabled: !!slug,
    refetchInterval: (query) =>
      (query.state.data ?? []).some(isSessionRunActive) ? THREAD_LIST_ACTIVE_POLL_MS : false,
  })
}

/**
 * Cold-load resolver for `/agents/chat/:sessionId`: which visible agent owns a
 * given session.
 *
 * The stamped `hostedAgentId` and the per-agent rail caches only exist after a
 * prior visit; a hard refresh (or a fresh tab) of a foreign OPEN thread starts
 * with all of them empty, and `/api/list-sessions` never returns another user's
 * thread — so the module cannot tell which agent to render and the thread falls
 * through to the uniform 404. This walks the (small, access-filtered) roster
 * and asks each agent's thread list — which DOES return open threads to their
 * readers (B6) — whether it contains the session, priming that agent's rail
 * cache along the way so the thread rail and `useSessionSync` pinning reuse it.
 */
export function useResolveHostedAgentForSession(
  sessionId: string | null,
  agents: HostedAgentSummary[] | undefined,
  enabled: boolean
) {
  const queryClient = useQueryClient()
  return useQuery<HostedAgentSummary | undefined>({
    queryKey: [...queryKeys.hostedAgents, "resolve-session", sessionId ?? ""],
    enabled: enabled && !!sessionId && !!agents?.length,
    staleTime: HOSTED_AGENTS_STALE_TIME,
    queryFn: async () => {
      for (const agent of agents ?? []) {
        const sessions = await listHostedAgentSessionsApi(agent.slug)
        queryClient.setQueryData(queryKeys.hostedAgentSessions(agent.slug), sessions)
        if (sessions.some((session) => session.id === sessionId)) return agent
      }
      return undefined
    },
  })
}
